import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import styled, {css} from 'styled-components'

export type TooltipPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right'

export interface TooltipProps {
  content: string | React.ReactNode
  children: React.ReactNode
  isLight?: boolean
  offsetTop?: string
  offsetRight?: string
  offsetBottom?: string
  offsetLeft?: string
  wrapContent?: boolean
  position: TooltipPosition
  disabled?: boolean
  allowFocus?: boolean
  active?: boolean
  className?: string
  asPortal?: boolean
  delayMs?: number
  interactive?: boolean
}

interface StyledContentProps {
  isLight?: boolean
  offsetTop?: string
  offsetRight?: string
  offsetBottom?: string
  offsetLeft?: string
  wrapContent?: boolean
  position?: TooltipPosition
  triggerRect?: DOMRect
}

const ToolTipWrapper = styled.div<StyledContentProps>`
  position: relative;
  min-height: 30px;
  white-space: nowrap;
  font-size: ${({theme}) => theme.fontSizeS};
  line-height: ${({theme}) => theme.lineHeightCompact};
  text-align: center;
  border-radius: ${({theme}) => theme.borderRadius};
  padding: 5px 10px;
  box-shadow: ${({theme}) => theme.shadowDepth2};
  z-index: 999;

  ${props =>
    (props.isLight &&
      css`
        background-color: ${({theme}) => theme.white};
        color: ${({theme}) => theme.black};
      `) ||
    (!props.isLight &&
      css`
        background-color: ${({theme}) => theme.black};
        color: ${({theme}) => theme.white};
      `)};

  ${props =>
    props.offsetLeft
      ? css`
          margin-left: ${props.offsetLeft};
        `
      : null};
  ${props =>
    props.offsetRight
      ? css`
          margin-right: ${props.offsetRight};
        `
      : null};
  ${props =>
    props.offsetTop
      ? css`
          margin-top: ${props.offsetTop};
        `
      : null};
  ${props =>
    props.offsetBottom
      ? css`
          margin-bottom: ${props.offsetBottom};
        `
      : null};
  ${props =>
    props.wrapContent
      ? css`
          white-space: normal;
        `
      : null};
`

// TODO this is a WIP. introduced during AF card builder work to support
// tooltips that were otherwise hidden
// Needs to support all positions
// calc(${props.triggerRect?.height}px + 45%)
const PortalTooltip = styled(ToolTipWrapper)`
  max-width: 1000px;
  ${props =>
    props.position === 'bottom'
      ? css`
          transform: translate(
            calc(-50%),
            calc(${props.triggerRect?.height}px + 10px)
          );
        `
      : props.position === 'right'
        ? css`
            transform: translate(
              calc(${props.triggerRect?.width}px + 10px),
              -5px
            );
          `
        : props.position === 'bottom-right'
          ? css`
              transform: translate(
                0,
                calc(${props.triggerRect?.height}px + 15px)
              );
            `
          : props.position === 'left'
            ? css`
                transform: translate(-200px, -5px);
              ` // 200px is a bit of a magic number, but it's doing the trick for now
            : props.position === 'top'
              ? css`
                  transform: translate(-50%, -100%);
                `
              : props.position === 'bottom-left'
                ? css`
                    transform: translate(calc(-100% - 10px), 15px);
                  `
                : null};
`

const StyledTooltip = styled(ToolTipWrapper)`
  max-width: 1000px;
  position: absolute;
  ${props =>
    (props.position === 'top' &&
      css`
        left: 50%;
        top: -10px;
        transform: translate(-50%, -100%);
      `) ||
    (props.position === 'top-left' &&
      css`
        left: 0;
        top: -10px;
        transform: translate(0, -100%);
      `) ||
    (props.position === 'top-right' &&
      css`
        right: 0;
        top: -10px;
        transform: translate(0, -100%);
      `) ||
    (props.position === 'bottom' &&
      css`
        left: 50%;
        bottom: -10px;
        transform: translate(-50%, 100%);
      `) ||
    (props.position === 'bottom-left' &&
      css`
        left: 0;
        bottom: -10px;
        transform: translate(0, 100%);
      `) ||
    (props.position === 'bottom-right' &&
      css`
        right: 0;
        bottom: -10px;
        transform: translate(0, 100%);
      `) ||
    (props.position === 'left' &&
      css`
        left: -10px;
        top: 50%;
        transform: translate(-100%, -50%);
      `) ||
    (props.position === 'right' &&
      css`
        right: -10px;
        top: 50%;
        transform: translate(100%, -50%);
      `)};
`

const StyledSpan = styled.span`
  position: relative;
  outline: none;
`

export const Tooltip = ({
  children,
  content,
  position,
  isLight,
  offsetTop,
  offsetRight,
  offsetBottom,
  offsetLeft,
  wrapContent,
  allowFocus,
  disabled,
  active,
  className,
  asPortal,
  delayMs,
  interactive
}: TooltipProps) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const elementRef = useRef<HTMLElement>(null)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const leaveTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (disabled) {
      setIsHovered(false)
      setIsFocused(false)
    }
  }, [disabled])

  let triggerRect: DOMRect | undefined = undefined
  if (asPortal) {
    triggerRect = elementRef.current?.getBoundingClientRect()
  }

  const handleMouseEnter = () => {
    if (delayMs) {
      hoverTimerRef.current = setTimeout(() => {
        setIsHovered(true)
      }, delayMs)
    } else {
      setIsHovered(true)
    }
  }

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    if (interactive) {
      leaveTimerRef.current = setTimeout(() => {
        setIsHovered(false)
        setIsFocused(false)
      }, 150)
    } else {
      setIsHovered(false)
      setIsFocused(false)
    }
  }

  const handlePopupMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
    setIsHovered(true)
  }

  const handlePopupMouseLeave = () => {
    setIsHovered(false)
    setIsFocused(false)
  }

  return content ? (
    <StyledSpan
      onFocus={() => setIsFocused(true)}
      onMouseDown={e => {
        // prevent the onFocus handler from firing if the delayMs prop is set
        // without this, the tooltip flashes up momentarily even if the delay time has not passed
        // this can be changed if that case is handled
        if (delayMs !== undefined) e.preventDefault()
      }}
      onBlur={() => setIsFocused(false)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => setIsFocused(false)}
      tabIndex={allowFocus ? 0 : -1}
      className={`${className ?? ''} tooltip`}
      ref={elementRef}
    >
      <>
        {children}
        {(active !== undefined ? active : isHovered || isFocused) &&
        !disabled ? (
          asPortal ? (
            createPortal(
              <div
                style={{
                  position: 'fixed',
                  zIndex: 99,
                  pointerEvents: interactive ? 'auto' : 'none',
                  top: triggerRect?.top,
                  left: triggerRect?.left
                }}
              >
                <PortalTooltip
                  position={position}
                  isLight={isLight}
                  triggerRect={triggerRect}
                  offsetTop={offsetTop}
                  offsetRight={offsetRight}
                  offsetBottom={offsetBottom}
                  offsetLeft={offsetLeft}
                  wrapContent={wrapContent}
                  onMouseEnter={interactive ? handlePopupMouseEnter : undefined}
                  onMouseLeave={interactive ? handlePopupMouseLeave : undefined}
                >
                  {content}
                </PortalTooltip>
              </div>,
              document.body
            )
          ) : (
            <StyledTooltip
              position={position}
              isLight={isLight}
              offsetTop={offsetTop}
              offsetRight={offsetRight}
              offsetBottom={offsetBottom}
              offsetLeft={offsetLeft}
              wrapContent={wrapContent}
              onMouseEnter={interactive ? handlePopupMouseEnter : undefined}
              onMouseLeave={interactive ? handlePopupMouseLeave : undefined}
            >
              {content}
            </StyledTooltip>
          )
        ) : null}
      </>
    </StyledSpan>
  ) : (
    <>{children}</>
  )
}
